import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SLIDES = [
  { src: "https://images.unsplash.com/photo-1735012314265-667c49f45bf4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", name: "Mercedes-AMG GT", stage: "STAGE 1", hp: 58, nm: 90, bar: 0.30, ecu: "Bosch MED17.7.2" },
  { src: "https://images.unsplash.com/photo-1614605637622-84a362f981f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", name: "Porsche 911 Carrera", stage: "STAGE 1", hp: 35, nm: 60, bar: 0.25, ecu: "Bosch MED17.1.11" },
  { src: "https://images.unsplash.com/photo-1605475300318-c377291697ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", name: "VW Golf GTI", stage: "STAGE 1", hp: 40, nm: 70, bar: 0.30, ecu: "Bosch MED17.5" },
  { src: "https://images.pexels.com/photos/32923909/pexels-photo-32923909.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", name: "Abarth 595", stage: "STAGE 1", hp: 35, nm: 55, bar: 0.35, ecu: "Marelli 8GMW" },
  { src: "https://images.pexels.com/photos/19871522/pexels-photo-19871522.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", name: "Mercedes Sprinter", stage: "STAGE 1", hp: 35, nm: 90, bar: 0.28, ecu: "Bosch EDC17CP46" },
  { src: "https://images.unsplash.com/photo-1606739211185-2c846d734a6d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", name: "Tracteur · Agri", stage: "STAGE 1", hp: 25, nm: 120, bar: 0.20, ecu: "Bosch EDC17CV41" },
];

const HEX = Array.from({ length: 30 }, () =>
  Array.from({ length: 7 }, () => Math.floor(Math.random() * 256).toString(16).padStart(2, "0").toUpperCase()).join(" ")
);

export const HeroShowcase = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);
  const s = SLIDES[i];

  return (
    <div className="relative rounded-md overflow-hidden border border-white/10 glass" data-testid="hero-showcase" style={{ height: 440 }}>
      {/* Car carousel (crossfade + ken-burns) */}
      <AnimatePresence>
        <motion.img
          key={i}
          src={s.src}
          alt={s.name}
          initial={{ opacity: 0, scale: 1.05, filter: "blur(10px)" }}
          animate={{ opacity: 0.88, scale: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, scale: 1.01, filter: "blur(6px)" }}
          transition={{ duration: 1.7, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      {/* Brand gradient + telemetry grid */}
      <div className="hero-overlay absolute inset-0 bg-gradient-to-t from-ink-base via-transparent to-transparent" />
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />

      {/* ECU read scan line — the soul of the project */}
      <div className="scan-line" />

      {/* Live hex data stream (ECU binary) */}
      <div className="absolute top-0 bottom-0 right-3 w-24 overflow-hidden opacity-25 pointer-events-none hidden sm:block">
        <div className="data-stream font-mono text-[8px] leading-[1.6] text-aurora-cyan">
          {[...HEX, ...HEX].map((l, k) => <div key={k}>{l}</div>)}
        </div>
      </div>

      {/* Top: live read + stage badge */}
      <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 glass rounded-sm">
        <span className="w-2 h-2 rounded-full bg-signal-error animate-pulse-glow" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-white">Lecture ECU · Live</span>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={`stage-${i}`}
          initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="absolute top-4 right-4 px-3 py-1.5 bg-aurora-cyan"
          data-testid="hero-stage-badge"
        >
          <span className="font-display font-bold text-xs tracking-widest text-white">{s.stage}</span>
        </motion.div>
      </AnimatePresence>

      {/* Car name + ECU id */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`label-${i}`}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.45 }}
          className="absolute left-4 bottom-[104px]"
        >
          <div className="font-display text-2xl font-bold uppercase tracking-tight text-white" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>{s.name}</div>
          <div className="font-mono text-[10px] text-aurora-cyan tracking-widest uppercase">ECU · {s.ecu}</div>
        </motion.div>
      </AnimatePresence>

      {/* Progress segments */}
      <div className="absolute bottom-[92px] right-4 flex gap-1.5">
        {SLIDES.map((_, k) => (
          <span key={k} className={`h-1 rounded-full transition-all duration-500 ${k === i ? "w-6 bg-aurora-cyan" : "w-1.5 bg-white/30"}`} />
        ))}
      </div>

      {/* Telemetry */}
      <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-3">
        {[["+" + s.hp, "HP"], ["+" + s.nm, "Nm"], ["+" + s.bar.toFixed(2), "bar"]].map(([v, u]) => (
          <motion.div
            key={u + i}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="glass rounded-sm px-3 py-2"
          >
            <div className="font-display text-xl font-bold text-aurora-cyan">{v}</div>
            <div className="font-mono text-[9px] text-zinc-400 uppercase tracking-widest">{u}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
